import { useState, useEffect } from 'react';
import { Store } from '../types/store';
import { getStores, createStore, deleteStore, updateStore, syncStoresFromPo } from '../services/storeApi';
import { Plus, Pencil, Trash2, RefreshCcw } from 'lucide-react';
import CreateStoreModal from '../components/modals/CreateStoreModal';
import EditStoreModal from '../components/modals/EditStoreModal';
import DeleteConfirmationModal from '../components/modals/DeleteConfirmationModal';
import { showSuccessToast, showErrorToast } from '../utils/toast';

export default function StoresPage() {
    const [stores, setStores] = useState<Store[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [regionalManage_mFilter, setRegionalManage_mFilter] = useState('all');
    const [area_managerFilter, setArea_managerFilter] = useState('all');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [selectedStore, setSelectedStore] = useState<Store | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [storeToDelete, setStoreToDelete] = useState<Store | null>(null);
    const [syncing, setSyncing] = useState(false);

    useEffect(() => {
        fetchStores();
    }, [searchTerm]);

    const fetchStores = async () => {
        try {
            const { data } = await getStores();
            setStores(data.data);
            setLoading(false);
        } catch (err) {
            showErrorToast('Failed to fetch stores');
            setError('Failed to fetch stores');
            setLoading(false);
        }
    };

    const handleSyncFromPo = async () => {
        if (syncing) return;

        setSyncing(true);
        try {
            await syncStoresFromPo();
            await fetchStores();
            showSuccessToast('Stores synced from PO successfully');
        } catch (error) {
            showErrorToast('Failed to sync stores from PO');
        } finally {
            setSyncing(false);
        }
    };

    // Get unique managers for filter dropdowns
    const regional_mnagers = stores
        .filter(store => store.regional_manager)
        .map(store => ({
            id: store.regional_manager!.id,
            name: store.regional_manager!.name
        }))
        .filter((v, i, a) => a.findIndex(t => t.id === v.id) === i);

    const area_managers = stores
        .filter(store => store.area_manager)
        .map(store => ({
            id: store.area_manager!.id,
            name: store.area_manager!.name
        }))
        .filter((v, i, a) => a.findIndex(t => t.id === v.id) === i);

    // Filter stores based on all criteria
    const filteredStores = stores.filter(store => {
        const matchesSearch = searchTerm === '' || (
            (store.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (store.storenumber?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (store.area?.toLowerCase() || '').includes(searchTerm.toLowerCase())
        );

        const matchesRegional_manager =
            regionalManage_mFilter === 'all' ||
            store.regional_manager?.name === regionalManage_mFilter;

        const matchesArea_manager =
            area_managerFilter === 'all' ||
            store.area_manager?.name === area_managerFilter;

        return matchesSearch && matchesRegional_manager && matchesArea_manager;
    });

    // Calculate pagination
    const totalPages = Math.ceil(filteredStores.length / itemsPerPage);
    const paginatedStores = filteredStores.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handleCreateStore = async (storeData: any) => {
        try {
            await createStore(storeData);
            await fetchStores();
            setIsCreateModalOpen(false);
            showSuccessToast('Store created successfully');
        } catch (error) {
            showErrorToast('Failed to create store');
            setError('Failed to create store');
        }
    };

    const handleEdit = (store: Store) => {
        setSelectedStore(store);
        setIsEditModalOpen(true);
    };

    const handleUpdate = async (id: number, storeData: Partial<Store>) => {
        try {
            await updateStore(id, storeData);
            await fetchStores();
            setIsEditModalOpen(false);
            showSuccessToast('Store updated successfully');
        } catch (error) {
            showErrorToast('Failed to update store');
            setError('Failed to update store');
        }
    };

    const handleDelete = async (store: Store) => {
        setStoreToDelete(store);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!storeToDelete) return;

        try {
            await deleteStore(storeToDelete.id);
            await fetchStores();
            setIsDeleteModalOpen(false);
            setStoreToDelete(null);
            showSuccessToast('Store deleted successfully');
        } catch (error) {
            showErrorToast('Failed to delete store');
            setError('Failed to delete store');
        }
    };

    // Add pagination controls component
    const PaginationControls = () => (
        <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 py-3 sm:px-6">
            <div className="flex flex-1 justify-between sm:hidden">
                <button
                    onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40"
                >
                    Previous
                </button>
                <button
                    onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                    disabled={currentPage === totalPages}
                    className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40"
                >
                    Next
                </button>
            </div>
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                        Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                        <span className="font-medium">
                            {Math.min(currentPage * itemsPerPage, filteredStores.length)}
                        </span>{' '}
                        of <span className="font-medium">{filteredStores.length}</span> results
                    </p>
                </div>
                <div>
                    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                        <button
                            onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center rounded-l-md px-3 py-2 text-sm text-gray-500 dark:text-gray-400 ring-1 ring-inset ring-gray-300 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 focus:z-20 disabled:opacity-40"
                        >
                            Prev
                        </button>
                        {[...Array(totalPages)].map((_, i) => (
                            <button
                                key={i + 1}
                                onClick={() => setCurrentPage(i + 1)}
                                className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold focus:z-20 ${currentPage === i + 1
                                    ? 'bg-indigo-600 text-white dark:bg-indigo-500 ring-1 ring-inset ring-indigo-600'
                                    : 'text-gray-900 dark:text-gray-200 ring-1 ring-inset ring-gray-300 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                                    }`}
                            >
                                {i + 1}
                            </button>
                        ))}
                        <button
                            onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                            disabled={currentPage === totalPages}
                            className="relative inline-flex items-center rounded-r-md px-3 py-2 text-sm text-gray-500 dark:text-gray-400 ring-1 ring-inset ring-gray-300 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 focus:z-20 disabled:opacity-40"
                        >
                            Next
                        </button>
                    </nav>
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[200px]">
                <div className="text-gray-500">Loading...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-[200px]">
                <div className="text-red-500 dark:text-red-400">{error}</div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 sm:px-0 text-gray-900 dark:text-gray-100">
            <div className="mb-6 flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Stores</h1>
                    <p className="text-gray-500 dark:text-gray-400">Manage store locations</p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleSyncFromPo}
                        disabled={syncing}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 border transition-colors ${syncing ? 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-500 dark:border-gray-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-800'}`}
                    >
                        <RefreshCcw size={18} className={syncing ? 'animate-spin' : ''} />
                        {syncing ? 'Syncing...' : 'Sync from PO'}
                    </button>
                    <button
                        onClick={() => setIsCreateModalOpen(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400 flex items-center gap-2"
                    >
                        <Plus size={20} />
                        Add Store
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="mb-4 flex flex-col md:flex-row gap-4">
                <input
                    type="text"
                    placeholder="Search stores..."
                    className="w-full md:w-1/3 px-4 py-2 border rounded-lg bg-white dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />

                <select
                    className="w-full md:w-1/4 px-4 py-2 border rounded-lg bg-white dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100"
                    value={regionalManage_mFilter}
                    onChange={(e) => setRegionalManage_mFilter(e.target.value)}
                >
                    <option value="all">All Regional Managers</option>
                    {regional_mnagers.map(manager => (
                        <option key={manager.id} value={manager.name}>{manager.name}</option>
                    ))}
                </select>

                <select
                    className="w-full md:w-1/4 px-4 py-2 border rounded-lg bg-white dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100"
                    value={area_managerFilter}
                    onChange={(e) => setArea_managerFilter(e.target.value)}
                >
                    <option value="all">All Area Managers</option>
                    {area_managers.map(manager => (
                        <option key={manager.id} value={manager.name}>{manager.name}</option>
                    ))}
                </select>
            </div>

            <div className="bg-white dark:bg-gray-900 shadow-sm rounded-lg overflow-hidden ring-1 ring-gray-200 dark:ring-gray-800">
                <div className="overflow-x-auto">
                    <div className="max-h-[600px] overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                            <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Store Number
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Email
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Type
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Area
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Regional Manager
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Area Manager
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                                {paginatedStores.map((store) => (
                                    <tr key={store.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 dark:text-gray-100">{store.storenumber}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                {store.name}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-500 dark:text-gray-300">{store.store_email}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                                {store.store_type}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                                            {store.area}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                                            {store.regional_manager?.name || '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                                            {store.area_manager?.name || '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                onClick={() => handleEdit(store)}
                                                className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 mr-3"
                                            >
                                                <Pencil size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(store)}
                                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <PaginationControls />
            </div>

            <CreateStoreModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onSubmit={handleCreateStore}
                regionalManagers={regional_mnagers}
                areaManagers={area_managers}
            />

            <EditStoreModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onSubmit={handleUpdate}
                store={selectedStore}
                regionalManagers={regional_mnagers}
                areaManagers={area_managers}
            />

            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setStoreToDelete(null);
                }}
                onConfirm={confirmDelete}
                itemType="Store"
                itemName={storeToDelete?.name || ''}
            />
        </div>
    );
} 